/**
 * Platform Admin — Helix VC Internal Only
 *
 * Sidebar entry: "Platform Admin" (visible to is_staff / is_superuser only)
 * Contains platform-level config that clients never see: portal type,
 * menu builder, feature flags, data import, analytics datasets, help layout.
 */
import { useState } from 'react';
import {
  Settings, Zap, Upload, LifeBuoy, BarChart3, Users, LayoutDashboard,
  ChevronRight,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import PerformanceSettings from '../../components/settings/PerformanceSettings';
import {
  useAdminSettings,
  PlatformTab,
  FeaturesTab,
  DataImportTilesTab,
  HelpLayoutTab,
  AnalyticsTab,
  DashboardTabsTab,
  CustomerCardTab,
  CustomerProfileLayoutTab,
} from '../../components/admin/settings';

type Section =
  | 'platform' | 'features' | 'data-import' | 'analytics'
  | 'dashboard-tabs' | 'customer-card' | 'customer-profile'
  | 'performance' | 'help';

const SECTIONS: { key: Section; label: string; icon: React.ComponentType<{ className?: string }>; superOnly?: boolean }[] = [
  { key: 'platform', label: 'Platform Config', icon: Settings },
  { key: 'features', label: 'Features & Modules', icon: Zap },
  { key: 'dashboard-tabs', label: 'Dashboard Visibility', icon: LayoutDashboard },
  { key: 'customer-card', label: 'Customer Card', icon: Users },
  { key: 'customer-profile', label: 'Customer Profile', icon: Users },
  { key: 'data-import', label: 'Data Import', icon: Upload },
  { key: 'performance', label: 'Performance Config', icon: BarChart3 },
  { key: 'analytics', label: 'Analytics Datasets', icon: BarChart3 },
  { key: 'help', label: 'Help Layout', icon: LifeBuoy, superOnly: true },
];

export default function PlatformAdminPage() {
  const { user } = useAuth();
  const isSuperuser = Boolean(user?.is_superuser);
  const isStaff = Boolean(user?.is_staff);
  const [activeSection, setActiveSection] = useState<Section>('platform');
  const {
    platformSettings, setPlatformSettings,
    barrattFlags, setBarrattFlags,
    purchasingTabs, setPurchasingTabs, savePurchasingTabs,
    qualityTabs, setQualityTabs, saveQualityTabs,
    dashboardTabs, setDashboardTabs,
    customerDashboardTabs, setCustomerDashboardTabs,
    customerCardFields, setCustomerCardFields, saveCustomerCardFields,
    dataImportTiles, setDataImportTiles, saveDataImportTiles,
    saving,
    savePlatformSettings, saveDashboardTabs,
  } = useAdminSettings();

  if (!isStaff && !isSuperuser) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">You do not have access to Platform Admin.</p>
      </div>
    );
  }

  const visibleSections = SECTIONS.filter(s => !s.superOnly || isSuperuser);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Platform Admin</h1>
          <p className="text-muted-foreground text-sm mt-1">Internal platform configuration — Helix team only</p>
        </div>
        <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-semibold uppercase">VC Internal</span>
      </div>

      <div className="flex gap-6">
        {/* Left sidebar */}
        <nav className="w-56 shrink-0 space-y-1 sticky top-6 self-start max-h-[calc(100vh-8rem)] overflow-y-auto">
          {visibleSections.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setActiveSection(key)}
              className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded-md transition-colors ${
                activeSection === key
                  ? 'bg-primary/10 text-primary font-semibold'
                  : 'text-muted-foreground hover:bg-accent hover:text-foreground'
              }`}
            >
              <Icon className="h-4 w-4 shrink-0" />
              <span className="truncate">{label}</span>
              {activeSection === key && <ChevronRight className="h-3 w-3 ml-auto" />}
            </button>
          ))}
        </nav>

        {/* Content area */}
        <div className="flex-1 min-w-0">
          {activeSection === 'platform' && (
            <PlatformTab
              platformSettings={platformSettings} saving={saving}
              onPlatformSettingsChange={setPlatformSettings} onSave={savePlatformSettings}
              purchasingTabs={purchasingTabs} onPurchasingTabsChange={setPurchasingTabs} onSavePurchasingTabs={savePurchasingTabs}
              qualityTabs={qualityTabs} onQualityTabsChange={setQualityTabs} onSaveQualityTabs={saveQualityTabs}
            />
          )}
          {activeSection === 'features' && (
            <FeaturesTab
              platformSettings={platformSettings} barrattFlags={barrattFlags} saving={saving}
              onPlatformSettingsChange={setPlatformSettings} onBarrattFlagsChange={setBarrattFlags} onSave={savePlatformSettings}
            />
          )}
          {activeSection === 'dashboard-tabs' && (
            <DashboardTabsTab
              dashboardTabs={dashboardTabs} customerDashboardTabs={customerDashboardTabs} saving={saving}
              onDashboardTabsChange={setDashboardTabs} onCustomerDashboardTabsChange={setCustomerDashboardTabs} onSave={saveDashboardTabs}
            />
          )}
          {activeSection === 'customer-card' && (
            <CustomerCardTab customerCardFields={customerCardFields} saving={saving} onCustomerCardFieldsChange={setCustomerCardFields} onSave={saveCustomerCardFields} />
          )}
          {activeSection === 'customer-profile' && <CustomerProfileLayoutTab />}
          {activeSection === 'data-import' && (
            <DataImportTilesTab dataImportTiles={dataImportTiles} saving={saving} onDataImportTilesChange={setDataImportTiles} onSave={saveDataImportTiles} />
          )}
          {activeSection === 'performance' && <PerformanceSettings />}
          {activeSection === 'analytics' && <AnalyticsTab />}
          {activeSection === 'help' && isSuperuser && <HelpLayoutTab />}
        </div>
      </div>
    </div>
  );
}
